import PropTypes from 'prop-types';
import React, { Component, useEffect, useState, useMemo } from 'react';

import { ExternalLink, Plus, Trash } from 'lucide-react';
import { Input, Button, InputGroup, InputGroupText, DefaultTable, DefaultTooltip, FormSection, ButtonIcon } from 'twyne-new.ui'

import { PureSwitch } from 'components/form/Switch';

const propTypes = {
  value: PropTypes.string,
  domain: PropTypes.value,
  onChange: PropTypes.func,
  onChangeDefault: PropTypes.func,
};

export default class DomainAliases extends Component {
  state = {
    aliases: [],
    wildCardAlaises: {},
  }

  columns = [{
    dataField: 'alias',
    text: 'Alias',
    headerClassName: 'w-[60%]',
    formatter: (_, row, index) => <this.AliasInput key={index} data={row} />,
  }, {
    dataField: '',
    text: 'Default',
    headerClassName: 'w-[15%]',
    formatter: (_, row) => <this.DefaultDomainSwitch data={row} />,
  }, {
    dataField: '',
    text: 'Mult Domain',
    headerClassName: 'w-[20%]',
    formatter: (_, row) => <>
      <this.WildCardDomain data={row} />
    </>,
  }, {
    dataField: '',
    text: '',
    className: 'w-8',
    formatter: (_, row, index) => <>
      <this.DeleteButton index={index} />
    </>,
  }]

  handleChangeAliases = change => {
    const { aliases, wildCardAlaises } = this.state
    change(aliases, wildCardAlaises)
    const domainAliases = aliases.map(({ alias }) => JSON.stringify(alias)).concat(Object.values(wildCardAlaises).map(alias => JSON.stringify(alias))).join()
    this.props.onChange(domainAliases)
    this.setState({ aliases })
  }

  useAlias = ({ alias, index }) => {
    const { domain, onChangeDefault } = this.props
    const [value, setValue] = useState(alias)
    const [isDomain, setIsDomain] = useState(false)
    const [wildCard, setWildCard] = useState(this.state.wildCardAlaises[index])

    useEffect(() => {
      setIsDomain(alias === domain)
    }, [domain])

    useEffect(() => {
      setValue(alias)
    }, [alias])

    return {
      value, isDomain,
      setValue,
      setIsDomain(isDomain) {
        onChangeDefault(value)
        setIsDomain(isDomain)
      },
      setWildCard: isWildCard => {
        setWildCard(isWildCard)
        if (isWildCard) {
          this.setState(prevState => ({ wildCardAlaises: { ...prevState.wildCardAlaises, [index]: `*.${value}` } }), () => this.handleChangeAliases(() => { }))
          return
        }
        this.setState(prevState => {
          delete prevState.wildCardAlaises[index]
          return { wildCardAlaises: prevState.wildCardAlaises }
        }, () => this.handleChangeAliases(() => { }))
      },
      wildCard,
    }
  }

  AliasInput = ({ data }) => {
    const { value, isDomain, setIsDomain, setValue } = this.useAlias(data)
    const url = useMemo(() => value && new URL(`https://${value}`).href, [value])

    const handleChangeAlias = () => {
      isDomain && setIsDomain(true)
      this.handleChangeAliases(() => {
        data.alias = value
      })
    }

    return <div className='pr-4'>
      <InputGroup>
        <InputGroupText>https://</InputGroupText>
        <Input
          name="domainAlias"
          value={value}
          onChange={({ target }) => setValue(target.value)}
          onBlur={handleChangeAlias}
        />
        <DefaultTooltip content='Open link'>
          <Button type='button' variant='light' color="accent" disabled={!url} onClick={() => window.open(url, '_blank')}>
            <ExternalLink size={16} />
          </Button>
        </DefaultTooltip>
      </InputGroup>
    </div>
  }

  DefaultDomainSwitch = ({ data }) => {
    const { isDomain, value, setIsDomain } = this.useAlias(data)

    const handleChangeDefaultDomain = () => {
      this.props.onChangeDefault(value)
      setIsDomain(true)
    }

    return <PureSwitch value={isDomain} onChange={handleChangeDefaultDomain} />
  }

  WildCardDomain = ({ data }) => {
    const { wildCard, setWildCard } = this.useAlias(data)

    const handleChangeWildCard = event => {
      setWildCard(event.target.checked)
    }

    return <PureSwitch value={wildCard} onChange={handleChangeWildCard} />
  }

  DeleteButton = ({ index }) => {
    const handleDelete = () => {
      this.handleChangeAliases((aliases, wildCardAlaises) => {
        delete wildCardAlaises[index]
        aliases.splice(index, 1);
      })
    }

    return <ButtonIcon onClick={handleDelete} content='Delete' >
      <Trash size={16} />
    </ButtonIcon>
  }

  AddButton = () => {
    const handleAdd = () => {
      this.handleChangeAliases(aliases => {
        aliases.push({ alias: '', index: aliases.length })
      })
    }

    return <Button
      color="accent"
      onClick={handleAdd}
      size='sm'
    >
      <Plus size={14} />
      Add
    </Button>
  }

  Table = () => {
    const { state: { aliases }, props: { value, domain }, columns } = this;

    useEffect(() => {
      if (value && typeof value === 'string') {
        const aliases = []
        const wildCards = []
        const wildCardAlaises = {}

        const parseAliasToken = token => {
          const trimmed = token.trim()
          if (!trimmed) return ''
          try {
            return JSON.parse(trimmed)
          } catch {
            return trimmed
          }
        }

        value.split(',').forEach((alias, index) => {
          const aliasPerse = parseAliasToken(alias)
          if (aliasPerse.includes('*.')) {
            wildCards.push(aliasPerse)
            return
          }

          aliases.push({
            index,
            alias: aliasPerse,
          })
        });
        aliases.forEach(alias => {
          const index = wildCards.findIndex(wildCard => alias.alias === wildCard.replace('*.', ''))
          if (index !== -1) {
            wildCardAlaises[alias.index] = wildCards[index]
          }
        })
        this.setState({ aliases, wildCardAlaises })
      }
    }, [])

    return <DefaultTable
      data={aliases}
      columns={columns}
      noDataIndication={() => ''}
      className='max-h-[280px] overflow-y-auto'
      bordered
    />
  }

  render() {
    return <>
      <div className='flex justify-between items-center'>
        <FormSection>
          Domains Aliases
        </FormSection>
        <this.AddButton />
      </div>
      <this.Table />
    </>
  }
}

DomainAliases.propTypes = propTypes
DomainAliases.defaultProps = {}
